/**
 * imageCompress.js — system-wide client-side image resizing.
 *
 * Every place in the app that uploads an image (fuel receipts, inspection
 * photos, vehicle attachments, OCR scans, company logos, ...) goes through
 * `compressImageTo1MB` so the resulting base64 data URL is ≤1MB. Phone camera
 * shots can be 3-12MB raw — this utility iteratively downscales + re-encodes
 * the image as JPEG until it fits.
 *
 * Backends still enforce the 1MB cap as defense-in-depth. Non-image files
 * (PDF, etc.) bypass the resize and are passed through untouched.
 */

const TARGET_BYTES = 1024 * 1024;          // 1MB hard cap
const SOFT_BUDGET = 1_050_000;             // small slack for base64 padding
const RAW_INPUT_HARD_LIMIT = 25 * 1024 * 1024; // 25MB rejection ceiling

const DEFAULT_STEPS = [
    { maxEdge: 1600, quality: 0.82 },
    { maxEdge: 1280, quality: 0.78 },
    { maxEdge: 1024, quality: 0.72 },
    { maxEdge: 800, quality: 0.65 },
    { maxEdge: 640, quality: 0.55 },
    { maxEdge: 480, quality: 0.5 },
];

/** Estimate the decoded byte length of a base64 data URL. */
function dataUrlBytes(dataUrl) {
    if (!dataUrl) return 0;
    const i = dataUrl.indexOf(",");
    return Math.ceil((dataUrl.length - i - 1) * 3 / 4);
}

/** Read a File into a base64 data URL. */
function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(new Error("file read failed"));
        r.readAsDataURL(file);
    });
}

/** Resize+re-encode an image File via canvas. Returns a JPEG data URL. */
async function _resizeOnce(file, maxEdge, quality) {
    const dataUrl = await readFileAsDataURL(file);
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const w0 = img.width, h0 = img.height;
            const scale = Math.min(1, maxEdge / Math.max(w0, h0));
            const w = Math.max(1, Math.round(w0 * scale));
            const h = Math.max(1, Math.round(h0 * scale));
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.onerror = () => reject(new Error("image decode failed"));
        img.src = dataUrl;
    });
}

/**
 * Compress an image File to a base64 data URL ≤1MB.
 * @returns {Promise<{dataUrl:string, bytes:number, ratio:number, originalBytes:number}>}
 * @throws if the file isn't an image, exceeds 25MB, or can't be brought under 1MB.
 */
export async function compressImageTo1MB(file, opts = {}) {
    if (!file) throw new Error("no file");
    if (!file.type?.startsWith("image/")) {
        throw new Error("画像ファイルを選択してください");
    }
    if (file.size > RAW_INPUT_HARD_LIMIT) {
        throw new Error(`画像は${Math.round(RAW_INPUT_HARD_LIMIT / 1024 / 1024)}MB以下にしてください`);
    }
    const steps = opts.steps || DEFAULT_STEPS;
    let dataUrl = "";
    let bytes = Infinity;
    for (const s of steps) {
        dataUrl = await _resizeOnce(file, s.maxEdge, s.quality);
        bytes = dataUrlBytes(dataUrl);
        if (bytes <= SOFT_BUDGET) break;
    }
    if (bytes > TARGET_BYTES) {
        throw new Error("画像を1MB以下に圧縮できませんでした。別の写真をお試しください。");
    }
    return {
        dataUrl,
        bytes,
        originalBytes: file.size,
        ratio: file.size > 0 ? +(bytes / file.size).toFixed(3) : 0,
    };
}

/**
 * Pass-through wrapper that auto-compresses image files but leaves non-image
 * files (PDF, etc.) as raw base64 data URLs. Used by attachment uploaders that
 * accept mixed file types.
 */
export async function readFileSmart(file) {
    if (!file) throw new Error("no file");
    if (file.type?.startsWith("image/")) {
        const { dataUrl } = await compressImageTo1MB(file);
        return dataUrl;
    }
    // Cap non-image uploads at 5MB to keep request bodies sane
    if (file.size > 5 * 1024 * 1024) {
        throw new Error("ファイルは5MB以下にしてください");
    }
    return readFileAsDataURL(file);
}

/** Format a byte count for display. */
export function fmtKB(bytes) {
    return `${(bytes / 1024).toFixed(0)} KB`;
}
